import { Meta } from '@storybook/angular';
import { AbstractControl } from '../shared/controls/abstract-control/abstract-control';
import { parseStorybookText as parse } from '@helpers';

type SummaryDetail = {
  summary: string;
  detail: string;
};

export const FONT_SIZE_SMALL: SummaryDetail = {
  summary: '--font-size-small',
  detail: '1.4rem (14px)',
};
export const FONT_SIZE_MEDIUM: SummaryDetail = {
  summary: '--font-size-medium',
  detail: '1.6rem (16px)',
};

export const GAP_SMALL_VALUE: SummaryDetail = {
  summary: '--gap-small',
  detail: '9px',
};

export const GAP_SEMI_VALUE: SummaryDetail = {
  summary: '--gap-semi',
  detail: '15px',
};

export const GAP_MEDIUM_VALUE: SummaryDetail = {
  summary: '--gap-medium',
  detail: '20px',
};

export const MARGIN_EXTRA_LARGE_VALUE: SummaryDetail = {
  summary: '--margin-extra-large',
  detail: '40px',
};

export const ABSTRACT_CONTROL_ARG_TYPES: Meta<AbstractControl<unknown, unknown>> = {
  argTypes: {
    $disabled: {
      name: 'isDisabled',
      description: 'Para deshabilitar el control desde el selector',
      control: false,
      table: {
        type: { summary: parse('boolean | undefined') },
        defaultValue: { summary: 'undefined' },
      },
    },

    $readonly: {
      name: 'isReadonly',
      description: 'Para activar el modo readonly, que visualmente es distino a deshabilitado',
      control: false,
      table: {
        type: { summary: parse('boolean | undefined') },
        defaultValue: { summary: 'undefined' },
      },
    },

    $label: {
      name: 'label',
      description: 'Label a mostrar, si es null solo se mostrará el control',
      control: false,
      table: {
        type: { summary: parse('string | null') },
        defaultValue: { summary: 'null' },
      },
    },

    $labelFontSize: {
      name: 'labelFontSize',
      description: 'Tamaño de fuente del label',
      control: false,
      table: {
        type: { summary: 'string' },
        defaultValue: {
          summary: FONT_SIZE_MEDIUM.summary,
          detail: FONT_SIZE_MEDIUM.detail,
        },
      },
    },

    $inputFontSize: {
      name: 'inputFontSize',
      description: 'Tamaño de fuente del input',
      control: false,
    },

    $labelWeight: {
      name: 'labelWeight',
      description: 'Font-weight del label',
      control: false,
      table: {
        type: { summary: parse("'normal' | '500' | '600' | 'bold'") },
        defaultValue: { summary: 'normal' },
      },
    },

    $labelColor: {
      name: 'labelColor',
      description: 'Color del label',
      control: false,
      table: {
        type: { summary: 'CssColor' },
        defaultValue: { summary: '--black-white' },
      },
    },

    $labelWrap: {
      name: 'labelWrap',
      description: 'Si es _true_ el label se posicionará en la siguiente línea cuando haya poco espacio',
      control: false,
      table: {
        type: { summary: 'boolean' },
        defaultValue: { summary: 'false' },
      },
    },

    $innerPadding: {
      name: 'innerPadding',
      description: 'Padding del elemento _label_',
      control: false,
      table: {
        type: { summary: parse('string | undefined') },
        defaultValue: { summary: 'undefined' },
      },
    },

    $gap: {
      name: 'gap',
      description: 'Espacio entre el control y el label',
      control: false,
      table: {
        type: { summary: 'string' },
        defaultValue: {
          summary: "En función de 'controlPosition'",
          detail: `default y opposite: ${ GAP_SEMI_VALUE.summary } (${ GAP_SEMI_VALUE.detail })\ntop y bottom: ${ GAP_SMALL_VALUE.summary } (${ GAP_SMALL_VALUE.detail })`,
        },
      },
    },

    $controlPosition: {
      name: 'controlPosition',
      description: 'Posición del control con respecto al label',
      control: false,
      table: {
        type: {
          summary: parse("'default' | 'opposite' | 'top' | 'bottom'"),
          detail: "'default': Posición horizontal por defecto (depende del tipo de control)\n\n'opposite': El control se posicionará en el lado opuesto a la posición por defecto\n\n'top': El control se posicionará arriba del label\n\n'bottom': El control se posicionará debajo del label",
        },
        defaultValue: { summary: 'derfault' },
      },
    },

    $verticalAlignment: {
      name: 'verticalAlignment',
      description: 'Determina la alineación vertical cuando el control esta arriba o abajo del label',
      control: false,
      table: {
        type: { summary: parse("'start' | 'center' | 'end'") },
        defaultValue: { summary: 'start' },
      },
    },

    $tooltip: {
      name: 'tooltip',
      description: 'Tooltip cuando el puntero del mouse entra estando el control **habilitado**',
      control: false,
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: '' },
      },
    },

    $tooltipWhenDisabled: {
      name: 'tooltipWhenDisabled',
      description: 'Tooltip cuando el puntero del mouse entra estando el control **deshabilitado**',
      control: false,
      table: {
        type: { summary: 'string' },
        defaultValue: { summary: '' },
      },
    },
  },
};
